import {
  policyRateNote,
  policyRateValue,
  type InsightCitation,
  type InsightKind,
  type InsightSource,
  type IsoTimestamp,
  type MarketSnapshot,
  type ResearchedInsight,
} from '@nav/shared';

import type { SflFundCode } from './sflProducts';

/**
 * Market context for the Siji Fixed Link page.
 *
 * This is NOT uploaded data and does not come from the API. NAV figures on
 * the same page are live; everything here is either a free live API quote
 * (via `applyLiveMacros`, below) or a hand-written editorial fallback.
 *
 * `macros`: Yield SBN 10Y, Inflasi Domestik (IDR) and Voting FOMC, Ekspektasi
 * Pemangkasan (USD) were all removed — none have a free public JSON API, so
 * they'd only ever be static and silently go stale. What's left per product
 * is genuinely live: Rupiah via Yahoo Finance FX and BI-Rate via bi.go.id
 * (IDR; the BI-Rate cell is appended only when the server has a quote, never
 * as an editorial guess); Fed Funds Rate via FRED (needs a free key, else
 * falls back to this editorial value) and US Treasury 10Y via Yahoo Finance
 * (USD).
 *
 * `insights`: for the same reason, there is no hand-written macro-causality
 * commentary left here — both cards shown under "Insight & Analisa" are
 * computed live from the fund's own NAV series in `insightsEngine.ts`
 * (`source: 'auto'`). This array is kept (currently empty per product) so
 * editorial commentary can be reintroduced later without a schema change.
 */

export type InsightTone = 'negative' | 'positive' | 'neutral' | 'accent';

export interface MacroItem {
  label: string;
  value: string;
  note: string;
}

export interface InsightItem {
  kicker: string;
  title: string;
  /** Markdown-lite: wrap emphasis in **double asterisks**. */
  body: string;
  tone: InsightTone;
  /**
   * Provenance. Three tiers, three visibly different badges, never styled alike:
   *
   *   'auto'       computed from uploaded NAV (insightsEngine.ts)
   *   'editorial'  hand-written by a person (this file)
   *   'researched' written by Claude, cited, approved by an analyst
   *
   * A reader who cannot tell which is which is the failure this whole design is
   * arranged to avoid.
   */
  source: InsightSource;
  /** Sources backing a researched claim. Empty for the other two tiers. */
  citations?: InsightCitation[];
  /** When a researched card was generated — it can be weeks old (decision D11). */
  generatedAt?: IsoTimestamp | null;
  /**
   * Which slot a researched card fills: `macro` (the outside world, cited) or
   * `fund` (the fund's own series). Drives the featured row's left/right
   * placement. Absent for the other two tiers.
   */
  kind?: InsightKind | null;
  /** Server row id of a researched card, for stable keys. */
  id?: number;
}

export interface MarketContext {
  asOfLabel: string;
  macros: MacroItem[];
  insights: InsightItem[];
  sourceNote: string;
}

const IDR: MarketContext = {
  asOfLabel: 'Konteks pasar',
  macros: [
    {
      label: 'Rupiah',
      value: 'Tertekan',
      note: 'Sempat mendekati Rp18.000/USD',
    },
  ],
  insights: [],
  sourceNote:
    'Sumber data kinerja: NAV bulanan live Siji Fixed Link (IDR) pada dashboard ini. Kurs Rupiah: Yahoo Finance (live). BI-Rate: Bank Indonesia (bi.go.id, live, harian; fallback statistik BIS). Insight & Analisa dihitung otomatis dari data NAV — tidak ada komentar editorial makro yang ditampilkan karena tidak ada sumber data publik gratis untuk memverifikasinya secara berkelanjutan.',
};

const USD: MarketContext = {
  asOfLabel: 'Konteks pasar',
  macros: [
    {
      label: 'Fed Funds Rate',
      value: '3,50%–3,75%',
      note: 'Ditahan 5x berturut-turut di 2026',
    },
    {
      label: 'US Treasury 10Y',
      value: '~4,69%',
      note: 'Bertahan tinggi sepanjang H1 2026',
    },
  ],
  insights: [],
  sourceNote:
    'Sumber data kinerja: NAV bulanan live Siji Fixed Link (Dollar) pada dashboard ini. Fed Funds Rate: FRED (live, perlu API key gratis) dengan fallback editorial. US Treasury 10Y: Yahoo Finance (live). Insight & Analisa dihitung otomatis dari data NAV — tidak ada komentar editorial makro yang ditampilkan karena tidak ada sumber data publik gratis untuk memverifikasinya secara berkelanjutan.',
};

const BY_CODE: Record<SflFundCode, MarketContext> = {
  SFFR: IDR,
  SFFD: USD,
};

export function marketContextFor(code: SflFundCode): MarketContext {
  return BY_CODE[code];
}

// ─── Live overlay ────────────────────────────────────────────────────────────
//
// Free, keyless-or-free-key public data (Yahoo Finance for FX, FRED for US
// rates — see marketDataService.ts on the server). When available, it
// replaces the matching editorial macro card's value/note; when not
// (no internet, FRED key not configured), the editorial value stands
// unchanged. Either way the card always has something to show.

function formatChangeNote(changePct30d: number | null): string {
  if (changePct30d == null) return 'Live';
  const arrow = changePct30d >= 0 ? '▲' : '▼';
  return `${arrow} ${Math.abs(changePct30d).toFixed(2)}% / 30 hari · Live`;
}

/**
 * Returns a copy of `context.macros` with the live-sourced cards swapped in
 * where data is available. `snapshot` is `undefined` while the query is still
 * loading — the editorial value stays put rather than flashing empty.
 */
export function applyLiveMacros(
  code: SflFundCode,
  macros: MacroItem[],
  snapshot: MarketSnapshot | undefined,
): MacroItem[] {
  if (!snapshot) return macros;

  if (code === 'SFFR') {
    const withFx = snapshot.usdIdr
      ? macros.map((m) =>
          m.label === 'Rupiah'
            ? {
                ...m,
                value: `Rp${Math.round(snapshot.usdIdr!.value).toLocaleString('id-ID')}/USD`,
                note: formatChangeNote(snapshot.usdIdr!.changePct30d),
              }
            : m,
        )
      : macros;
    // BI-Rate has no editorial fallback on purpose: a policy rate that is
    // months stale reads as a fact, so the cell exists only when the server
    // has a quote. Without one the strip is a single Rupiah cell, as before.
    if (!snapshot.biRate) return withFx;
    const q = snapshot.biRate;
    return [...withFx, { label: 'BI-Rate', value: policyRateValue(q), note: policyRateNote(q) }];
  }

  if (code === 'SFFD') {
    return macros.map((m) => {
      if (m.label === 'Fed Funds Rate' && snapshot.fedFundsRate) {
        return {
          ...m,
          value: `${snapshot.fedFundsRate.value.toFixed(2)}%`,
          note: `Effective rate · ${formatChangeNote(snapshot.fedFundsRate.changePct30d)}`,
        };
      }
      if (m.label === 'US Treasury 10Y' && snapshot.ust10y) {
        return {
          ...m,
          value: `${snapshot.ust10y.value.toFixed(2)}%`,
          note: formatChangeNote(snapshot.ust10y.changePct30d),
        };
      }
      return m;
    });
  }

  return macros;
}

/**
 * Maps a published `ResearchedInsight` onto the shape the grid renders.
 */
export function toInsightItem(insight: ResearchedInsight): InsightItem {
  return {
    kicker: insight.kicker,
    title: insight.title,
    body: insight.body,
    tone: insight.tone,
    source: 'researched',
    citations: insight.citations,
    generatedAt: insight.generatedAt,
    kind: insight.kind,
    id: insight.id,
  };
}

// ─── Disclaimer ──────────────────────────────────────────────────────────────
//
// Conditional, and deliberately so. The previous single string asserted the
// dashboard uses "tanpa AI, tanpa API berbayar" — true before this feature, and
// false the moment a researched card is published. But it is also false in the
// other direction: the feature ships dark, and can be switched off at any time
// by removing an API key, at which point an AI-mentioning disclaimer would be
// claiming something the page no longer does.
//
// So the page states what it is actually showing, and self-corrects both ways.
// Rolling this back is not a deploy.

const DISCLAIMER_COMMON =
  'Nilai pada kartu Konteks Pasar bersumber dari sumber publik gratis (Yahoo Finance, FRED, Bank Indonesia) ' +
  'selama tersedia; bila tidak, nilai editorial terakhir yang tercatat tetap ditampilkan ' +
  'sebagai fallback. Kinerja masa lalu tidak menjamin hasil di masa depan. Dashboard ini ' +
  'bersifat informatif untuk mendukung pemahaman kinerja fund, bukan merupakan rekomendasi ' +
  'atau saran investasi.';

const DISCLAIMER_COMPUTED_ONLY =
  'Setiap kartu pada bagian "Insight & Analisa" bertanda "Data Live" dan dihitung otomatis ' +
  'dari data NAV bulanan yang ter-upload (tanpa AI, tanpa API berbayar). ';

const DISCLAIMER_WITH_RESEARCH =
  'Kartu bertanda "Data Live" dihitung otomatis dari data NAV bulanan yang ter-upload. ' +
  'Kartu bertanda "Riset AI" disusun oleh model bahasa (Claude) berdasarkan penelusuran ' +
  'sumber publik, diperiksa secara otomatis terhadap data NAV, dan baru tampil setelah ' +
  'ditinjau dan disetujui oleh analis. Setiap klaim eksternal mencantumkan tautan sumbernya, ' +
  'dan tanggal penyusunan tertera pada kartu. ';

/** The disclaimer for what this page is actually rendering right now. */
export function sflDisclaimer(items: InsightItem[]): string {
  const hasResearched = items.some((i) => i.source === 'researched');
  return (hasResearched ? DISCLAIMER_WITH_RESEARCH : DISCLAIMER_COMPUTED_ONLY) + DISCLAIMER_COMMON;
}
